import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { CaseFieldValueEntity } from '../../../domain/entities/case-field-value.entity';
import { UpdateCaseFieldValuesCommand } from './update-case-field-values.command';

/**
 * Use case for updating case field values.
 * Transforms the field values record into entities and calls the repository.
 */
@Injectable({ providedIn: 'root' })
export class UpdateCaseFieldValuesUseCase {
  private readonly _repository = inject(CasesRepository);

  /**
   * Executes the update case field values use case.
   * @param command - Command with caseId and fieldValues
   * @returns Observable void
   */
  public execute(command: UpdateCaseFieldValuesCommand): Observable<void> {
    const fieldValueEntities = this._transformToEntities(command.fieldValues);
    return this._repository.updateCaseFieldValues(command.caseId, fieldValueEntities);
  }

  /**
   * Transforms the field values record into an array of CaseFieldValueEntity.
   * Handles JSON stringification for all field types.
   * @param fieldValues - Record of versionFieldId -> value
   * @returns Array of CaseFieldValueEntity
   */
  private _transformToEntities(fieldValues: Record<string, unknown>): CaseFieldValueEntity[] {
    return Object.entries(fieldValues).map(([versionFieldId, value]) => {
      const entity = new CaseFieldValueEntity();
      entity.versionFieldId = versionFieldId;
      entity.value = this._stringifyValue(value);
      return entity;
    });
  }

  /**
   * Converts a field value to a JSON string.
   * Special handling for Date objects and other types.
   * @param value - The value to stringify
   * @returns JSON string representation
   */
  private _stringifyValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Handle Date objects - convert to ISO string
    if (value instanceof Date) {
      return JSON.stringify(value.toISOString());
    }

    // Handle arrays and objects - JSON stringify
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    // Handle primitives - convert to string then JSON stringify to ensure proper escaping
    return JSON.stringify(String(value));
  }
}
